<template>
  <div class="magazine-page">
    <AppHeader />

    <div class="magazine-layout">
      <!-- 左侧年份选择 -->
      <aside class="magazine-years">
        <!-- 年份骨架屏 -->
        <template v-if="yearLoading">
          <div v-for="n in 8" :key="n" class="magazine-year-skeleton"></div>
        </template>
        <!-- 年份列表 -->
        <template v-else>
          <div
            v-for="year in yearList"
            :key="year"
            class="magazine-year-item"
            :class="{ 'magazine-year-item--active': currentYear === year }"
            @click="selectYear(year)"
          >
            {{ year }}年
          </div>
        </template>
      </aside>

      <!-- 右侧杂志列表 -->
      <main class="magazine-list">
        <div v-if="loading" class="magazine-skeleton">
          <div v-for="n in 3" :key="n" class="magazine-skeleton__card">
            <div class="magazine-skeleton__top">
              <div class="magazine-skeleton__cover"></div>
              <div class="magazine-skeleton__info">
                <div class="magazine-skeleton__title"></div>
                <div class="magazine-skeleton__line magazine-skeleton__line--short"></div>
                <div class="magazine-skeleton__line"></div>
                <div class="magazine-skeleton__line"></div>
                <div class="magazine-skeleton__line"></div>
              </div>
            </div>
            <div class="magazine-skeleton__brand">
              <div class="magazine-skeleton__brand-title"></div>
              <div class="magazine-skeleton__brand-list">
                <div class="magazine-skeleton__brand-item"></div>
                <div class="magazine-skeleton__brand-item"></div>
                <div class="magazine-skeleton__brand-item"></div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="item in magazineList"
          :key="item.id"
          class="magazine-card"
          @click="openReader(item)"
        >
          <div class="magazine-card__top">
            <img class="magazine-cover" :src="item.cover" alt="杂志封面" />
            <div class="magazine-info">
              <h3 class="magazine-issue">{{ item.name }}</h3>
              <div class="magazine-nav-list">
                <div
                  v-for="nav in item.navList.slice(0, 5)"
                  :key="nav.id"
                  class="magazine-nav-item"
                >
                  <span class="magazine-nav-type">{{ nav.navTypeName }}</span>
                  <span class="magazine-nav-name">{{ nav.navName.length > 14 ? nav.navName.slice(0, 14) + '...' : nav.navName }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="magazine-brand-section">
            <div class="magazine-brand-title">本期品牌</div>
            <div class="magazine-brand-list">
              <span
                v-for="brand in item.brandList.slice(0, 5)"
                :key="brand.id"
                class="magazine-brand-name"
              >{{ brand.name }}</span>
            </div>
          </div>
        </div>

        <div v-if="magazineList.length === 0 && !loading" class="magazine-empty">
          暂无该年份杂志
        </div>
      </main>
    </div>

    <!-- 阅读器弹层 -->
    <MagazineReader
      v-if="currentEmagazineCode"
      :emagazine-code="currentEmagazineCode"
      :code-token="codeToken"
      @close="currentEmagazineCode = ''; codeToken = '';"
    />
  </div>
</template>

<script>
import AppHeader from '../home/components/AppHeader.vue';
import MagazineReader from './components/MagazineReader.vue';
import { getMagazineList, getMagazineYearList } from '@/api/magazine';

export default {
  name: 'MagazineList',
  components: {
    AppHeader,
    MagazineReader
  },
  data() {
    return {
      yearList: [],
      yearLoading: false,
      currentYear: null,
      magazineList: [],
      loading: false,
      currentEmagazineCode: '',
      codeToken: '', // 存储 codeToken 参数
      navTypeMap: {
        1: '卷首',
        2: '数据',
        3: '聚焦',
        4: '快消',
        5: '话题',
        6: '科技'
      }
    };
  },
  mounted() {
    this.fetchYears();
    // 登录后返回：如果有购买标记，自动打开对应杂志阅读器
    const buyCode = localStorage.getItem('magazine_buy_code');
    const token = localStorage.getItem('token');
    if (buyCode && token) {
      this.currentEmagazineCode = buyCode;
    }
    // 如果 URL 参数中有 emagazineCode，也打开对应杂志
    const emagazineCode = this.$route.query.emagazineCode;
    if (emagazineCode) {
      this.currentEmagazineCode = emagazineCode;
      // 保存 codeToken 到 data
      this.codeToken = this.$route.query.codeToken || '';
      // 延迟清理 URL 参数，确保 reader 组件已经获取到 codeToken
      setTimeout(() => {
        this.$router.replace({ query: {} });
      }, 100);
    }
  },
  methods: {
    async fetchYears() {
      this.yearLoading = true;
      try {
        const res = await getMagazineYearList();
        const data = res.data;
        this.yearList = Array.isArray(data) ? data : (data.records || data.list || data.rows || []);
        if (this.yearList.length > 0) {
          this.currentYear = this.yearList[0];
          this.fetchMagazines();
        }
      } catch (err) {
        console.error('获取年份列表失败', err);
        this.yearList = [];
      } finally {
        this.yearLoading = false;
      }
    },
    selectYear(year) {
      this.currentYear = year;
      this.fetchMagazines();
    },
    async fetchMagazines() {
      if (!this.currentYear) return;
      this.loading = true;
      try {
        const res = await getMagazineList({
          year: this.currentYear,
          pageSize: 1000,
          pageNum: 1
        });
        if (res.code === 200 && res.data) {
          this.magazineList = res.data.records || [];
        } else {
          this.magazineList = [];
        }
      } catch (err) {
        console.error('获取杂志列表失败', err);
        this.magazineList = [];
      } finally {
        this.loading = false;
      }
    },
    formatIssue(num) {
      const n = parseInt(num, 10);
      return n < 10 ? `0${n}` : n;
    },
    openReader(item) {
      this.currentEmagazineCode = item.emagazineCode;
    }
  }
};
</script>

<style scoped>
@import '../home/assets/_variables.css';

.magazine-page {
  width: var(--page-width);
  min-height: 100vh;
  background-color: var(--color-white);
}

.magazine-layout {
  display: flex;
  width: var(--page-width);
  min-height: calc(100vh - 228px);
  background-color: var(--color-bg-page);
}

/* 左侧年份 */
.magazine-years {
  width: 140px;
  flex-shrink: 0;
  padding: 24px 0;
  background-color: var(--color-bg-page);
}

.magazine-year-item {
  padding: 16px 24px;
  color: var(--color-text-dark);
  font-size: var(--font-lg);
  font-family: var(--font-primary);
  cursor: pointer;
  transition: color 0.2s;
}

.magazine-year-item:hover {
  color: var(--color-primary);
}

.magazine-year-item--active {
  color: var(--color-primary);
  font-weight: var(--weight-bold);
}

/* 年份骨架屏 */
.magazine-year-skeleton {
  margin: 0 24px;
  height: 28px;
  border-radius: var(--radius-sm);
  margin-bottom: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

/* 右侧杂志列表 */
.magazine-list {
  flex: 1;
  padding: 24px;
}

.magazine-card {
  display: flex;
  flex-direction: column;
  background-color: var(--color-white);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  cursor: pointer;
  transition: box-shadow 0.25s;
}

.magazine-card__top {
  display: flex;
}

.magazine-card:hover {
  box-shadow: var(--shadow-overlay);
}

.magazine-cover {
  width: 200px;
  height: 280px;
  object-fit: cover;
  border-radius: var(--radius-md);
  flex-shrink: 0;
}

.magazine-info {
  flex: 1;
  margin-left: 24px;
  height: 280px;
  display: flex;
  flex-direction: column;
}

.magazine-issue {
  color: var(--color-black);
  font-size: var(--font-xl);
  font-family: var(--font-primary);
  font-weight: var(--weight-bold);
  margin: 0 0 16px;
}

.magazine-nav-list {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  gap: 8px;
}

.magazine-nav-item {
  display: flex;
  align-items: center;
}

.magazine-nav-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 32px;
  padding: 0 8px;
  background-color: var(--color-text-dark);
  color: var(--color-white);
  font-size: var(--font-sm);
  font-family: var(--font-primary);
  border-radius: var(--radius-sm);
  margin-right: 8px;
}

.magazine-nav-name {
  color: var(--color-text-dark);
  font-size: var(--font-base);
  font-family: var(--font-primary);
}

.magazine-brand-section {
  border-top: 1px solid #eeeeee;
  padding-top: 16px;
  margin-top: 24px;
}

.magazine-brand-title {
  position: relative;
  padding-left: 14px;
  color: var(--color-black);
  font-size: var(--font-lg);
  font-family: var(--font-primary);
  font-weight: var(--weight-bold);
  margin-bottom: 12px;
}

.magazine-brand-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 22px;
  background-color: var(--color-primary);
  border-radius: var(--radius-sm);
}

.magazine-brand-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 24px;
}

.magazine-brand-name {
  color: var(--color-text-gray);
  font-size: var(--font-base);
  font-family: var(--font-primary);
}

.magazine-empty {
  padding: 60px 0;
  text-align: center;
  color: var(--color-text-light);
  font-size: var(--font-lg);
  font-family: var(--font-primary);
}

/* 骨架屏 */
.magazine-skeleton__card {
  background-color: var(--color-white);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 24px;
}

.magazine-skeleton__top {
  display: flex;
}

.magazine-skeleton__cover {
  width: 200px;
  height: 280px;
  border-radius: var(--radius-md);
  flex-shrink: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

.magazine-skeleton__info {
  flex: 1;
  margin-left: 24px;
  height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.magazine-skeleton__title {
  width: 70%;
  height: 32px;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

.magazine-skeleton__line {
  width: 100%;
  height: 24px;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

.magazine-skeleton__line--short {
  width: 50%;
}

.magazine-skeleton__brand {
  border-top: 1px solid #eeeeee;
  padding-top: 16px;
  margin-top: 24px;
}

.magazine-skeleton__brand-title {
  width: 100px;
  height: 28px;
  border-radius: var(--radius-sm);
  margin-bottom: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

.magazine-skeleton__brand-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 24px;
}

.magazine-skeleton__brand-item {
  height: 24px;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
